import { ArrowRight, Calendar, Globe, Coins, Gift, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { t } from "@/utils/translations";
import { trackSignupStarted, trackEvent } from "@/utils/analytics";
import { trackHeroDemoClick } from "@/utils/analytics";
import { useSignupModal } from "@/contexts/SignupModalContext";
import OfferBadge from "@/components/offer/OfferBadge";

const CALENDLY_URL_PLACEHOLDER = "/book-demo";

const scrollToHowItWorks = () => {
  const el = document.getElementById("how-it-works");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const mockContractors = [
  { name: "Maria G.", phone: "+52 *** 4821", amount: "$2,400" },
  { name: "Raj P.", phone: "+91 *** 7390", amount: "$3,200" },
  { name: "Ana S.", phone: "+55 *** 1056", amount: "$1,800" },
  { name: "Oleg K.", phone: "+380 *** 6644", amount: "$2,600" },
  { name: "Fatima H.", phone: "+971 *** 2198", amount: "$2,900" },
  { name: "James L.", phone: "+63 *** 8832", amount: "$1,500" },
  { name: "Priya M.", phone: "+91 *** 4417", amount: "$3,800" },
  { name: "Carlos R.", phone: "+57 *** 3295", amount: "$2,200" },
];

const HeroSection = () => {
  const { language, isRTL } = useLanguage();
  const { openSignupModal } = useSignupModal();

  const handleStartPaying = () => {
    trackSignupStarted(t('homeHeroButton', language));
    openSignupModal('hero');
  };

  const handleSendBatch = () => {
    trackSignupStarted('Send batch (mockup)');
    openSignupModal('hero_send_batch_mockup');
  };

  const handleBookDemo = () => {
    trackHeroDemoClick();
    window.location.href = CALENDLY_URL_PLACEHOLDER;
  };

  return (
    <section className="relative pt-20 pb-20 lg:pt-36 lg:pb-36 overflow-hidden bg-[#1e1e3c]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Copy */}
          <div className={`${isRTL ? 'text-right' : ''}`}>
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-white/75 mb-8">
              {t('homeHeroLabel', language)}
            </span>

            <h1 className={`text-3xl sm:text-5xl md:text-6xl font-bold mb-8 leading-[1.1] text-white`}>
              {t('homeHeroTitle1', language)}
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-[#E5E7EB] mb-6 leading-relaxed max-w-xl">
              {t('homeHeroSubtitle', language)}
            </p>

            {/* 3-icon value strip — what you get, absorbed in 2s by bouncers */}
            <ul className="flex flex-wrap gap-x-5 gap-y-2 mb-6 text-xs sm:text-sm text-white/80">
              <li className="inline-flex items-center gap-1.5">
                <Globe className="h-4 w-4 text-[#E91E63]" aria-hidden />
                100+ countries
              </li>
              <li className="inline-flex items-center gap-1.5">
                <Coins className="h-4 w-4 text-[#E91E63]" aria-hidden />
                USDC or Other
              </li>
              <li className="inline-flex items-center gap-1.5">
                <Gift className="h-4 w-4 text-[#E91E63]" aria-hidden />
                First $10K In Transfers Free
              </li>
            </ul>

            <p className="text-white/75 text-[11px] uppercase tracking-wider mb-6">
              Operated by Flip Company AS · Built for global business payouts
            </p>

            <div className="mb-6">
              <button
                type="button"
                onClick={() => {
                  trackEvent('offer_badge_click', { location: 'hero' });
                  openSignupModal('hero_offer_badge');
                }}
                className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <OfferBadge variant="onDark" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-start gap-4 mb-4">
              <button
                onClick={handleStartPaying}
                className="btn-flip-primary group h-14 px-8 text-base rounded-xl"
              >
                {t('homeHeroButton', language)}
                <ArrowRight className={`ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={handleBookDemo}
                className="h-14 px-8 text-base rounded-xl border-2 border-white/60 text-white hover:bg-white/10 transition-colors flex items-center gap-2 font-semibold"
              >
                <Calendar className="h-5 w-5" />
                {t('homeHeroDemoButton', language)}
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                trackEvent('hero_scroll_cue_click', { target: 'how-it-works' });
                scrollToHowItWorks();
              }}
              className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-4 transition-colors"
            >
              See how it works
              <ChevronDown className="h-4 w-4" />
            </button>

            <p className="text-white/75 text-xs">{t('trustGlobalLine', language)}</p>
          </div>


          {/* Right — Product Mockup (entire card is clickable) */}
          <div className="hidden lg:block">
            <button
              type="button"
              onClick={handleSendBatch}
              className="w-full text-left bg-white rounded-2xl shadow-2xl p-5 -rotate-1 transform hover:rotate-0 transition-transform duration-500 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E91E63]"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100" aria-hidden="true">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">Batch Payout</p>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Preview</span>
                  </div>
                  <p className="text-xs text-gray-500">12 recipients · $28,400 total</p>
                </div>
                <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  All sent ✓
                </div>
              </div>

              {/* Contractor rows (illustrative) */}
              <div className="space-y-1.5 select-none" aria-hidden="true">
                {mockContractors.map((c) => (
                  <div key={c.name} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 text-xs">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#1e1e3c]/10 flex items-center justify-center text-[10px] font-bold text-[#1e1e3c]">
                        {c.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">{c.name}</span>
                        <span className="text-gray-400 ml-2">{c.phone}</span>
                      </div>
                    </div>
                    <span className="font-semibold text-gray-900">{c.amount}</span>
                  </div>
                ))}
              </div>

              {/* Visual CTA (parent handles click) */}
              <span
                className="block w-full mt-4 h-11 leading-[2.75rem] text-center rounded-xl bg-[#D81B60] text-white text-sm font-bold"
              >
                Send batch — $28,400 to 12 recipients
              </span>
            </button>
          </div>

          {/* Mobile mockup (simplified, entire card clickable) */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={handleSendBatch}
              className="w-full text-left bg-white rounded-2xl shadow-xl p-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E91E63]"
            >
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100" aria-hidden="true">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">Batch Payout</p>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Preview</span>
                </div>
                <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  All sent ✓
                </div>
              </div>
              <div className="space-y-1 select-none" aria-hidden="true">
                {mockContractors.slice(0, 4).map((c) => (
                  <div key={c.name} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-gray-50 text-xs">
                    <span className="font-medium text-gray-900">{c.name}</span>
                    <span className="font-semibold text-gray-900">{c.amount}</span>
                  </div>
                ))}
                <p className="text-center text-xs text-gray-600 py-1">+ 8 more</p>
              </div>
              <span
                className="block w-full mt-3 h-10 leading-[2.5rem] text-center rounded-xl bg-[#D81B60] text-white text-xs font-bold"
              >
                Send batch — $28,400 to 12 recipients
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
